import React, { useCallback, useEffect, useRef } from "react"
import moment from "moment"
import { useSnackbar } from "notistack"
import { useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import Account from "src/components/Topbar/Account"
import AdminNotifications from "src/components/Topbar/notifications/AdminNotifications"
import {
  acceptLegalAgreement,
  checkLegalAgreement,
} from "src/services/complexServices"
import { selectUser } from "src/redux/slices/authSlice"
import { selectConjunto } from "src/redux/slices/complexSlice"
import * as MaintenanceFirestoreService from "../../views/viewAdministrador/mantenimiento/services/firestore"
import * as IssueReportFirestoreService from "../../views/viewAdministrador/issuesReport/services/firestore"
import { Brand, ComplexSwitcher } from "./shared"

const Swal = require("sweetalert2")

const TERMS_AND_CONDITIONS_URL =
  "https://www.tglabsecuador.com/mi-casita#comp-krgvr8r7"
const TERMS_MODAL_HTML = `
  <p style="text-align: justify;">
    Para continuar, por favor revisa nuestros
    <a href="${TERMS_AND_CONDITIONS_URL}" target="_blank" style="color: #007bff; text-decoration: none;">
      Términos y Condiciones.
    </a><br/>
  </p>
  <label style="display: flex; align-items: center; gap: 8px;">
    <input type="checkbox" id="acceptTerms" />
    <span>He leído y acepto los términos y condiciones de uso.</span>
  </label>
`

const buildSwal = (customClass = {}) =>
  Swal.mixin({
    customClass: {
      confirmButton: "btn btn-success",
      cancelButton: "btn btn-danger",
      ...customClass,
    },
    buttonsStyling: false,
  })

const showLegalAgreementModal = async () => {
  const swal = buildSwal()
  const result = await swal.fire({
    title: "Términos y Condiciones",
    html: TERMS_MODAL_HTML,
    imageUrl: "https://cdn-icons-png.flaticon.com/512/10348/10348844.png",
    imageWidth: 150,
    imageHeight: 150,
    confirmButtonText: "Continuar",
    allowOutsideClick: false,
    allowEscapeKey: false,
    showCloseButton: false,
    didOpen: () => {
      const confirmButton = Swal.getConfirmButton()
      const checkbox = document.getElementById("acceptTerms")

      if (confirmButton) {
        confirmButton.disabled = true
      }

      checkbox?.addEventListener("change", event => {
        if (confirmButton) {
          confirmButton.disabled = !event.target.checked
        }
      })
    },
  })

  return {
    isConfirmed: result.isConfirmed,
    hasAccepted: Boolean(document.getElementById("acceptTerms")?.checked),
  }
}

const showFirestoreAlert = async ({
  title,
  text,
  imageUrl,
  route,
  snackbarMessage,
  history,
  enqueueSnackbar,
}) => {
  enqueueSnackbar(snackbarMessage, { variant: "error" })

  const result = await buildSwal().fire({
    title,
    text,
    icon: "warning",
    imageUrl,
    imageWidth: 400,
    imageHeight: 200,
    confirmButtonText: "Detalle",
  })

  if (result.isConfirmed) {
    history.push(route)
  }
}

const createPendingMaintenanceSubscriber = ({
  conjuntoId,
  history,
  enqueueSnackbar,
}) =>
  MaintenanceFirestoreService.suscriberPendingMaintenances(
    {
      next: querySnapshot => {
        if (querySnapshot.empty) {
          return
        }

        const item = querySnapshot.docs[0]?.data()

        if (!item) {
          return
        }

        showFirestoreAlert({
          title: "Existen mantenimientos Pendientes",
          text: item.Detalle,
          imageUrl: item.Imagen,
          route: "/administrador/mantenimiento",
          snackbarMessage: "Mantenimientos Pendientes",
          history,
          enqueueSnackbar,
        })
      },
    },
    conjuntoId
  )

const createIssueReportSubscriber = ({
  conjuntoId,
  history,
  enqueueSnackbar,
}) =>
  IssueReportFirestoreService.getAlertareporte(conjuntoId, {
    next: querySnapshot => {
      if (querySnapshot.empty) {
        return
      }

      const item = querySnapshot.docs[0]?.data()

      if (!item) {
        return
      }

      showFirestoreAlert({
        title: "Reporte de Daños Pendientes",
        text: item.Observaciones,
        imageUrl: item.Imagen,
        route: "/administrador/informeProblemas",
        snackbarMessage: "Reporte de Daños Pendientes",
        history,
        enqueueSnackbar,
      })
    },
    error: () => {
      enqueueSnackbar("Error al cargar los reportes de daños", {
        variant: "error",
      })
    },
  })

const initializeFirestoreSubscriptions = ({
  conjuntoId,
  history,
  enqueueSnackbar,
}) => {
  if (!conjuntoId) {
    return () => {}
  }

  const unsubscribePending = createPendingMaintenanceSubscriber({
    conjuntoId,
    history,
    enqueueSnackbar,
  })
  const unsubscribeIssueReport = createIssueReportSubscriber({
    conjuntoId,
    history,
    enqueueSnackbar,
  })

  return () => {
    if (typeof unsubscribePending === "function") {
      unsubscribePending()
    }

    if (typeof unsubscribeIssueReport === "function") {
      unsubscribeIssueReport()
    }
  }
}

const verifyUserLegalAgreement = async (userId, enqueueSnackbar) => {
  const agreementExists = await checkLegalAgreement(userId)

  if (agreementExists) {
    return true
  }

  const { isConfirmed, hasAccepted } = await showLegalAgreementModal()

  if (!isConfirmed || !hasAccepted) {
    enqueueSnackbar(
      "Debes aceptar los términos y condiciones para continuar.",
      {
        variant: "warning",
      }
    )

    return false
  }

  await acceptLegalAgreement(userId, {
    AceptaAcuerdoLegal: true,
    fecha: moment().format("YYYY-MM-DD HH:mm:ss"),
  })

  return true
}

const useAdminLayoutEffects = ({ userId, conjuntoId }) => {
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const cleanupRef = useRef(() => {})

  const initializeSubscriptions = useCallback(
    () =>
      initializeFirestoreSubscriptions({
        conjuntoId,
        history,
        enqueueSnackbar,
      }),
    [conjuntoId, history, enqueueSnackbar]
  )

  useEffect(() => {
    let active = true

    const setup = async () => {
      if (!userId) {
        return
      }

      const isAgreementValid = await verifyUserLegalAgreement(
        userId,
        enqueueSnackbar
      )

      if (!active || !isAgreementValid) {
        return
      }

      cleanupRef.current = initializeSubscriptions()
    }

    setup()

    return () => {
      active = false

      if (typeof cleanupRef.current === "function") {
        cleanupRef.current()
      }
    }
  }, [userId, initializeSubscriptions, enqueueSnackbar])
}

export const End = () => (
  <>
    <ComplexSwitcher />
    <AdminNotifications />
    <Account />
  </>
)

export const Effects = () => {
  const user = useSelector(selectUser)
  const conjunto = useSelector(selectConjunto)
  useAdminLayoutEffects({ userId: user.id, conjuntoId: conjunto.id })
  return null
}

export { Brand }
